import { RefObject, useEffect, useRef, useState } from 'react';

export type UseIntersectionOptions = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
};

export const useIntersection = (
  targetRef: RefObject<Element>,
  options: UseIntersectionOptions = {},
) => {
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const target = targetRef.current;

    if (!target) return () => {};

    // Cleanup any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create a new IntersectionObserver
    const observer = new IntersectionObserver(
      ([newEntry]) => {
        setEntry(old => {
          if (
            !old ||
            Math.abs(
              newEntry.intersectionRatio - (old.intersectionRatio || 0),
            ) > 0.01 // Prevent flickering by ignoring minor changes
          )
            return newEntry;
          return old;
        });
      },
      {
        root: options.root || null,
        rootMargin: options.rootMargin || '0px',
        threshold: options.threshold || 0,
      },
    );

    observer.observe(target);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [targetRef, options.root, options.rootMargin, options.threshold]);

  return entry;
};
